import * as React from 'react'
import i18next from 'i18next'
import type { CommonDeps } from '../base/CommonDeps'
import type { Nullable } from '../../../shared/lib/typeHelpers.no-deps'
import type { BUTTON_SIZES } from '../../../shared/webcomponents/zaptic-button'

type Props<T> = {
    cacheDisabled?: boolean
    deps: CommonDeps
    fetchTranslation: () => Promise<T>
    id: string | number
    language: string // Original content language
    onTranslate: (data: Nullable<T>) => void
    size?: (typeof BUTTON_SIZES)[number]
    tall?: boolean
}

export function TranslateButton<T>({
    cacheDisabled,
    deps,
    fetchTranslation,
    id,
    language,
    onTranslate,
    size,
    tall,
}: Props<T>) {
    const [isTranslating, setIsTranslating] = React.useState(false)
    const [isTranslationVisible, setIsTranslationVisible] = React.useState(false)
    const [cachedTranslationData, setCachedTranslationData] = React.useState<T>(null)
    const userLanguage = deps.language.split('-')[0] // Example of lang code format: es-ES

    const onClick = async (event: React.MouseEvent) => {
        event.stopPropagation()

        if (isTranslationVisible) {
            // See original clicked
            onTranslate(null)
            return setIsTranslationVisible(false)
        }

        if (cachedTranslationData && !cacheDisabled) {
            onTranslate(cachedTranslationData)
            return setIsTranslationVisible(true)
        }

        setIsTranslating(true)
        try {
            const translation = await fetchTranslation()
            setCachedTranslationData(translation)
            setIsTranslationVisible(true)

            onTranslate(translation)
        } finally {
            setIsTranslating(false)
        }
    }

    const canTranslate = !isTranslationVisible && language && language !== userLanguage

    if (!canTranslate && !isTranslationVisible) {
        return null
    }

    return (
        <zaptic-button
            type="link"
            size={size || 'small'}
            tall={tall}
            loading={isTranslating}
            data-testid={`translate-button-${id}-${canTranslate ? 'see-translation' : 'see-original'}`}
            onClick={onClick}
        >
            {isTranslationVisible
                ? i18next.t('appcore:common.seeOriginal')
                : i18next.t('appcore:common.seeTranslation')}
        </zaptic-button>
    )
}
